
import {
  takeEvery, put, call, takeLatest,
} from 'redux-saga/effects';

import { select } from 'redux-saga-test-plan/matchers';

import {
  MODALS, API_URLS, FETCH_OPTIONS,
} from '../constants';
import { requestGet, requestPost } from '../utils/request';
import { getMarkets } from '../selectors/common';
import { addMarketsName } from '../utils/common';
import { aGetMarketSettingsRequest, aGetPrematchMarketsByAccount, aGetTennisMarketSettingsRequest, aSavePrematchMarketsByAccount, aUpdateOddsDisabledStatus, aUpdateResultsDisabledStatus, aUpdateTennisOddsDisabledStatus, aUpdateTennisResultsDisabledStatus } from '../reducers';
import { aSetIsFetching, aSetOpenModal } from '../reducers/common';
import { aGetMarketSettingsSuccess, aGetPrematchMarketSuccess, aGetTennisMarketSettingsSuccess } from '../reducers/marketSettings';

/**
 * @namespace sagas/marketSettings
 *
 */
/**
 * @memberof sagas/marketSettings
 * @typedef getMarketSettings/response.data
 * @type {object}
 */
/**
 * @memberof sagas/marketSettings
 * @async
 */
function* getMarketSettingsSaga() {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_MARKET_SETTINGS,
      value: true,
    }));

    response = yield call(requestGet, API_URLS.GET_MARKET_SETTINGS, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetMarketSettingsSuccess({
        marketSettings: response.data.mainMarkets,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_MARKET_SETTINGS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_MARKET_SETTINGS,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/marketSettings
 * @typedef getTennisMarketSettings/response.data for tennis
 * @type {object}
 */
/**
 * @memberof sagas/marketSettings
 * @async
 */
function* getTennisMarketSettingsSaga() {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_MARKET_SETTINGS,
      value: true,
    }));

    response = yield call(requestGet, API_URLS.GET_TENNIS_MARKET_SETTINGS, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aGetTennisMarketSettingsSuccess({
        marketSettings: response.data.mainMarkets,
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_TENNIS_MARKET_SETTINGS,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_TENNIS_MARKET_SETTINGS,
      value: false,
    }));
  }
}

/**
 * update odds for tennis
 *
 * @param {object} action
 */
function* updateTennisOddsDisableSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_TENNIS_ODDS_DISABLED_STATUS,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.UPDATE_TENNIS_MARKET_SETTINGS_ODDS_STATUS,
      action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_TENNIS_ODDS_DISABLED_STATUS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_TENNIS_ODDS_DISABLED_STATUS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* updateOddsDisableSaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_ODDS_DISABLED_STATUS,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.UPDATE_MARKET_SETTINGS_ODDS_STATUS,
      action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_ODDS_DISABLED_STATUS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_ODDS_DISABLED_STATUS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* updateResultsDisablesaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_RESULTS_DISABLED_STATUS,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.UPDATE_MARKET_SETTINGS_RESULTS_STATUS,
      action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_RESULTS_DISABLED_STATUS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_RESULTS_DISABLED_STATUS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* updateTennisResultsDisablesaga(action) {
  let response;
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_TENNIS_RESULTS_DISABLED_STATUS,
      value: true,
    }));

    response = yield call(requestPost, API_URLS.UPDATE_TENNIS_MARKET_SETTINGS_RESULTS_STATUS,
      action.payload, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_TENNIS_RESULTS_DISABLED_STATUS,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_TENNIS_RESULTS_DISABLED_STATUS,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 * @memberof sagas/marketSettings
 * @async
 */
function* getPrematchMarketSaga(action) {
  try {
    if (!action.payload) return;
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_PREMATCH_MARKET,
      value: true,
    }));
    const accoundId = action.payload.roleId;
    const response = yield call(requestGet, `${API_URLS.GET_PREMATCH_MARKET}?accountId=${accoundId}`, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const AllMarkets = yield select(getMarkets);
      yield put(aGetPrematchMarketSuccess({
        markets: addMarketsName(AllMarkets, response.data.markets),
      }));
    }
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.GET_PREMATCH_MARKET,
      value: false,
    }));
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.GET_PREMATCH_MARKET,
      value: false,
    }));
  }
}

/**
 * @param {object} action
 */
function* updatePrematchMarketSaga(action) {
  try {
    yield put(aSetIsFetching({
      key: FETCH_OPTIONS.UPDATE_PREMATCH_MARKET,
      value: true,
    }));
    const body = {
      accountId: action.payload.roleId,
      markets: action.payload.permissionIds,
    };
    const response = yield call(requestPost, API_URLS.UPDATE_PREMATCH_MARKET,
      body, true);

    if (response.status === -1) {
      throw Error(response.error.message);
    } else {
      const AllMarkets = yield select(getMarkets);
      yield put(aGetPrematchMarketSuccess({
        markets: addMarketsName(AllMarkets, response.data.markets),
      }));
      yield put(aSetIsFetching({
        key: FETCH_OPTIONS.UPDATE_PREMATCH_MARKET,
        value: false,
      }));
    }
  } catch (error) {
    yield put(aSetOpenModal({
      modal: error.modal || MODALS.GENERAL_ERROR,
      errorMessage: error.modal ? error.message : MODALS.GENERAL_ERROR,
      key: FETCH_OPTIONS.UPDATE_PREMATCH_MARKET,
      value: false,
    }));
  }
}

/**
 * @memberof sagas/marketSettings
 * @async
 * @returns {void}
 */
export default function* marketSettingsSaga() {
  yield takeEvery(aGetMarketSettingsRequest, getMarketSettingsSaga);
  yield takeEvery(aGetTennisMarketSettingsRequest, getTennisMarketSettingsSaga);
  yield takeLatest(aUpdateOddsDisabledStatus, updateOddsDisableSaga);
  yield takeLatest(aUpdateTennisOddsDisabledStatus, updateTennisOddsDisableSaga);
  yield takeLatest(aUpdateResultsDisabledStatus, updateResultsDisablesaga);
  yield takeLatest(aUpdateTennisResultsDisabledStatus, updateTennisResultsDisablesaga);
  yield takeLatest(aGetPrematchMarketsByAccount, getPrematchMarketSaga);
  yield takeLatest(aSavePrematchMarketsByAccount, updatePrematchMarketSaga);
}

